// Normalise an internal route path to the trailing-slash form.
//
// Why: prerender writes each route as "<route>/index.html", so Netlify serves
// the page at "/route/" and 301-redirects "/route" -> "/route/". Canonicals and
// the sitemap already use the trailing-slash form (see SEOHead), but internal
// links did not — so every internal link made Googlebot burn a fetch on a
// redirect before reaching the destination, and Google ended up indexing the
// redirecting non-slash URLs it found in the markup instead of the canonical
// ones. On a crawl-starved site that split the signal for every page.
//
// Applied inside the shared link components (Navbar, Footer, CalculatorCard,
// Breadcrumbs, ...) rather than at each call site, so paths added to those link
// arrays later are normalised automatically.
export function internalHref(path: string): string {
  if (!path) return path;

  // Leave anything that is not a site-internal route path alone: absolute URLs,
  // protocol-relative URLs, mailto/tel, in-page anchors and query-only links.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  if (path.startsWith('//')) return path;
  if (path.startsWith('#') || path.startsWith('?')) return path;
  if (!path.startsWith('/')) return path;

  // Static assets are real files, not routes — they must not gain a slash.
  if (/\.[a-z0-9]+$/i.test(path.split(/[?#]/)[0])) return path;

  // Preserve any query string / hash while normalising only the path part.
  const match = path.match(/^([^?#]*)(.*)$/);
  const rawPath = match ? match[1] : path;
  const suffix = match ? match[2] : '';

  if (rawPath === '/') return `/${suffix}`;

  const withSlash = `${rawPath.replace(/\/+$/, '')}/`;
  return `${withSlash}${suffix}`;
}

export default internalHref;
